import { Copy, Receipt } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { useOutletContext } from 'react-router'

import { type ClienteDeAgencia, type CobroDeAgencia, cobros, importe, METODOS, useCobroDeAgencia, useFichaDeAgencia, useRecibosDeAgencia, useRefrescarAgencia } from '../../api/agencia'
import { ErrorDeLaApi } from '../../api/nucleo'
import { Aviso, Boton, Campo, Casilla, claseDeEntrada, Esqueleto, EstadoVacio, Modal, Selector, Tarjeta, Titulo } from '../../componentes/ui'
import type { Contexto } from '../Modulo'

/**
 * «Cobros» (CLAUDE.md §36, fase R5): cómo cobra la agencia a sus clientes, dentro de Wixy y con su dinero.
 * Su PayPal (una suscripción mensual a su plan, en su cuenta) y las formas a mano (Yape, Plin, transferencia)
 * que ella marca como pagadas. Cada pago deja un recibo con sus datos y alarga lo que el cliente tiene pagado.
 */
const mensajeDe = (e: unknown, siNo: string) => (e instanceof ErrorDeLaApi ? e.message : siNo)

/** Apuntar un pago recibido fuera de Wixy. Lo usan «Cobros» y la fila de cada cliente en «Mis clientes». */
export const ApuntarPago = ({ c, espacio, moneda, cerrar, hecho }: { c: Pick<ClienteDeAgencia, 'id' | 'nombre' | 'pagadoHasta'>; cerrar: () => void; espacio: number; hecho: (mensaje: string) => void; moneda: string }) => {
  const [meses, setMeses] = useState('1')
  const [cuanto, setCuanto] = useState('')
  const [metodo, setMetodo] = useState('yape')
  const [referencia, setReferencia] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [fallo, setFallo] = useState<null | string>(null)
  const enviar = async (e: FormEvent) => {
    e.preventDefault()
    setEnviando(true)
    setFallo(null)
    try {
      const r = await cobros(espacio).apuntar({ cuenta: c.id, meses: Number(meses), metodo, ...(cuanto.trim() ? { importe: cuanto.trim() } : {}), ...(referencia.trim() ? { referencia: referencia.trim() } : {}) })
      hecho(`Pago apuntado: recibo nº ${r.recibo.numero} por ${importe(r.recibo.importe, r.recibo.moneda)}. Se lo hemos mandado por correo.`)
    } catch (e) {
      setFallo(mensajeDe(e, 'No se ha podido apuntar el pago. Vuelve a intentarlo.'))
    } finally {
      setEnviando(false)
    }
  }
  return (
    <Modal
      ancho="max-w-md"
      cerrar={cerrar}
      pie={
        <div className="flex justify-end gap-2">
          <Boton onClick={cerrar}>Cancelar</Boton>
          <Boton cargando={enviando} form="apuntar-pago" tipo="primario" type="submit">
            Apuntar el pago
          </Boton>
        </div>
      }
      titulo={`Pago de «${c.nombre}»`}
    >
      <form className="flex flex-col gap-4" id="apuntar-pago" onSubmit={enviar}>
        <p className="text-[13px] text-tenue">
          {c.pagadoHasta ? `Tiene pagado hasta el ${new Date(c.pagadoHasta).toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' })}: los meses se suman desde ahí.` : 'Es su primer pago: los meses cuentan desde hoy.'}
        </p>
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium">Cómo te pagó</span>
          <Selector onChange={(e) => setMetodo(e.target.value)} value={metodo}>
            {['yape', 'plin', 'transferencia', 'efectivo', 'otro'].map((m) => (
              <option key={m} value={m}>
                {METODOS[m]}
              </option>
            ))}
          </Selector>
        </label>
        <Campo etiqueta="Meses que paga" inputMode="numeric" onChange={(e) => setMeses(e.target.value)} required value={meses} />
        <Campo ayuda={`En ${moneda}. Vacío: el precio de su plan por los meses.`} etiqueta="Importe" inputMode="decimal" onChange={(e) => setCuanto(e.target.value)} placeholder="El de su plan" value={cuanto} />
        <Campo ayuda="El número de operación, para encontrarlo luego." etiqueta="Referencia" onChange={(e) => setReferencia(e.target.value)} value={referencia} />
        {fallo && <Aviso tono="error">{fallo}</Aviso>}
      </form>
    </Modal>
  )
}

const Configuracion = ({ espacio, cobro }: { cobro: CobroDeAgencia; espacio: number }) => {
  const refrescar = useRefrescarAgencia(espacio)
  const [d, setD] = useState<Record<string, unknown>>({})
  const [guardando, setGuardando] = useState(false)
  const [mensaje, setMensaje] = useState<null | { tono: 'error' | 'ok'; texto: string }>(null)
  useEffect(() => {
    setD({
      moneda: cobro.moneda,
      paypalActivo: cobro.paypal.activo,
      paypalModo: cobro.paypal.modo,
      paypalClientId: cobro.paypal.clientId ?? '',
      paypalWebhookId: cobro.paypal.webhookId ?? '',
      paypalSecreto: '',
      yapeNumero: cobro.manuales.yape?.numero ?? '',
      yapeTitular: cobro.manuales.yape?.titular ?? '',
      plinNumero: cobro.manuales.plin?.numero ?? '',
      plinTitular: cobro.manuales.plin?.titular ?? '',
      banco: cobro.manuales.transferencia?.banco ?? '',
      titularDeLaCuenta: cobro.manuales.transferencia?.titular ?? '',
      cuenta: cobro.manuales.transferencia?.cuenta ?? '',
      cci: cobro.manuales.transferencia?.cci ?? '',
      instrucciones: cobro.manuales.instrucciones ?? '',
      diasDeGracia: String(cobro.diasDeGracia),
      suspenderAlVencer: cobro.suspenderAlVencer,
    })
  }, [cobro])
  const campo = (nombre: string) => ({ value: String(d[nombre] ?? ''), onChange: (e: { target: { value: string } }) => setD({ ...d, [nombre]: e.target.value }) })
  const hacer = async (accion: () => Promise<string>) => {
    setGuardando(true)
    setMensaje(null)
    try {
      setMensaje({ tono: 'ok', texto: await accion() })
      void refrescar()
    } catch (e) {
      setMensaje({ tono: 'error', texto: mensajeDe(e, 'No se ha podido guardar. Vuelve a intentarlo.') })
    } finally {
      setGuardando(false)
    }
  }
  const guardar = (e: FormEvent) => {
    e.preventDefault()
    void hacer(async () => {
      await cobros(espacio).guardar({ ...d, diasDeGracia: Number(d.diasDeGracia ?? 5) })
      setD((x) => ({ ...x, paypalSecreto: '' }))
      return 'Guardado.'
    })
  }
  return (
    <form className="flex flex-col gap-4" onSubmit={guardar}>
      <Tarjeta>
        <h2 className="font-display text-base font-semibold">Moneda y vencimientos</h2>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium">Moneda en la que cobras</span>
            <Selector {...campo('moneda')}>
              {cobro.monedas.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Selector>
          </label>
          <Campo ayuda="Tras vencer, cuántos días sigue funcionando sin pagar." etiqueta="Días de gracia" inputMode="numeric" {...campo('diasDeGracia')} />
        </div>
        <label className="mt-4 flex items-start gap-2 text-sm">
          <Casilla marcada={d.suspenderAlVencer === true} onChange={(e) => setD({ ...d, suspenderAlVencer: e.target.checked })} />
          <span>
            Poner en pausa a quien no paga
            <span className="block text-[13px] text-tenue">Pasados los días de gracia, su cuenta y sus webs se pausan solas. Al apuntar su pago, vuelven. Tres días antes de vencer le avisamos por correo, con tu nombre.</span>
          </span>
        </label>
      </Tarjeta>

      <Tarjeta>
        <h2 className="font-display text-base font-semibold">PayPal: suscripción mensual</h2>
        <p className="mt-1 text-[13px] text-tenue">
          Con tu propia app de PayPal (developer.paypal.com → Apps &amp; Credentials). El dinero va a tu cuenta; Wixy no lo toca. PayPal cobra suscripciones en USD, EUR o MXN.
        </p>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Client ID" {...campo('paypalClientId')} />
          <Campo ayuda={cobro.paypal.pista ? `Guardado (${cobro.paypal.pista}). Vacío: se queda el que hay.` : 'Se guarda cifrado y no vuelve a enseñarse.'} autoComplete="off" etiqueta="Secreto" type="password" {...campo('paypalSecreto')} />
          <Campo ayuda="El de la dirección de avisos de abajo, en tu app de PayPal." etiqueta="Webhook ID" {...campo('paypalWebhookId')} />
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium">Modo</span>
            <Selector {...campo('paypalModo')}>
              <option value="live">Real</option>
              <option value="sandbox">Pruebas (sandbox)</option>
            </Selector>
          </label>
        </div>
        <div className="mt-3 rounded-caja border border-linea bg-papel px-3 py-2 text-[13px]">
          <span className="text-tenue">Dirección de avisos (Webhooks) para tu app, con los eventos «Billing subscription» y «Payment sale completed»:</span>
          <div className="mt-1 flex items-center gap-2">
            <code className="min-w-0 truncate">{cobro.direccionDeAvisos}</code>
            <button aria-label="Copiar la dirección" className="text-violeta" onClick={() => void navigator.clipboard?.writeText(cobro.direccionDeAvisos)} type="button">
              <Copy aria-hidden className="size-4" />
            </button>
          </div>
        </div>
        <label className="mt-4 flex items-center gap-2 text-sm">
          <Casilla marcada={d.paypalActivo === true} onChange={(e) => setD({ ...d, paypalActivo: e.target.checked })} />
          Ofrecer a mis clientes pagar por PayPal
        </label>
        {!cobro.paypal.monedaAdmitida && <div className="mt-3"><Aviso tono="aviso">PayPal no cobra suscripciones en {cobro.moneda}: tus clientes solo verán las formas a mano.</Aviso></div>}
        <div className="mt-4 flex flex-wrap gap-2">
          <Boton disabled={!cobro.paypal.configurado || guardando} onClick={() => void hacer(async () => ((await cobros(espacio).probar()).suscripciones ? 'Tus credenciales funcionan y tu app puede cobrar suscripciones.' : 'Las credenciales valen, pero tu app no tiene activadas las suscripciones: actívalas en PayPal.'))} pequeno>
            Probar la conexión
          </Boton>
          <Boton disabled={!cobro.paypal.configurado || guardando} onClick={() => void hacer(async () => { const r = await cobros(espacio).sincronizar(); return r.creados ? `Listo: ${r.creados} plan${r.creados === 1 ? '' : 'es'} creado${r.creados === 1 ? '' : 's'} en tu PayPal.` : 'Tus planes ya estaban al día en PayPal.' })} pequeno>
            Preparar mis planes en PayPal
          </Boton>
        </div>
        <p className="mt-2 text-[13px] text-tenue">Cada vez que cambies el precio de un plan, pulsa «Preparar mis planes en PayPal»: hasta entonces ese plan no se puede pagar por PayPal.</p>
      </Tarjeta>

      <Tarjeta>
        <h2 className="font-display text-base font-semibold">Pagos a mano</h2>
        <p className="mt-1 text-[13px] text-tenue">Lo que pongas aquí lo ve tu cliente en «Pagos». Cuando te pague, apúntalo en «Mis clientes» y le llega el recibo.</p>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Yape: número" {...campo('yapeNumero')} />
          <Campo etiqueta="Yape: titular" {...campo('yapeTitular')} />
          <Campo etiqueta="Plin: número" {...campo('plinNumero')} />
          <Campo etiqueta="Plin: titular" {...campo('plinTitular')} />
          <Campo etiqueta="Banco" {...campo('banco')} />
          <Campo etiqueta="Titular de la cuenta" {...campo('titularDeLaCuenta')} />
          <Campo etiqueta="Número de cuenta" {...campo('cuenta')} />
          <Campo etiqueta="CCI" {...campo('cci')} />
        </div>
        <label className="mt-4 flex flex-col gap-1.5">
          <span className="text-[13px] font-medium">Instrucciones</span>
          <textarea className={claseDeEntrada()} maxLength={600} rows={3} {...campo('instrucciones')} placeholder="Por ejemplo: manda la captura del pago por WhatsApp al 999 999 999." />
        </label>
      </Tarjeta>

      {mensaje && <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso>}
      <div className="flex justify-end">
        <Boton cargando={guardando} tipo="primario" type="submit">
          Guardar
        </Boton>
      </div>
    </form>
  )
}

export const Cobros = () => {
  const { espacio } = useOutletContext<Contexto>()
  const { data: ficha, isPending: cargandoFicha } = useFichaDeAgencia(espacio)
  const { data: cobro, isPending, error } = useCobroDeAgencia(espacio)
  const { data: recibos } = useRecibosDeAgencia(espacio)
  if (!cargandoFicha && !ficha?.puede.cobros) return <Aviso tono="error">Esta cuenta no es una agencia, o no llevas sus cobros.</Aviso>
  return (
    <>
      <Titulo descripcion="Cobra a tus clientes con tu PayPal o a mano. El dinero es tuyo: Wixy guarda los recibos y avisa de los vencimientos." rotulo={ficha?.agencia.nombre}>
        Cobros
      </Titulo>
      {isPending ? <Esqueleto /> : error || !cobro ? <Aviso tono="error">No hemos podido leer tu configuración de cobro. Vuelve a intentarlo en un momento.</Aviso> : <Configuracion cobro={cobro} espacio={espacio} />}
      <h2 className="mt-8 mb-3 font-display text-lg font-semibold">Últimos recibos</h2>
      {!recibos ? (
        <Esqueleto />
      ) : recibos.length === 0 ? (
        <EstadoVacio descripcion="Cuando un cliente te pague por PayPal, o apuntes un pago a mano en «Mis clientes», aparecerá aquí." icono={<Receipt />} titulo="Todavía no hay recibos" />
      ) : (
        <Tarjeta sinRelleno>
          <ul className="divide-y divide-linea">
            {recibos.map((r) => (
              <li className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm" key={r.id}>
                <span className="min-w-0">
                  <strong>Nº {r.numero}</strong> · {r.cliente?.nombre ?? 'Cliente'} <span className="text-tenue">· {r.concepto}</span>
                </span>
                <span className="flex items-center gap-3 tabular-nums">
                  {importe(r.importe, r.moneda)} <span className="text-tenue">{METODOS[r.metodo] ?? r.metodo} · {new Date(r.fecha).toLocaleDateString('es')}</span>
                  <a className="text-violeta hover:underline" href={`/api/panel/recibo?id=${r.id}`} rel="noreferrer" target="_blank">
                    Ver
                  </a>
                </span>
              </li>
            ))}
          </ul>
        </Tarjeta>
      )}
    </>
  )
}
